/**
 * AREA_SCALE — per-shape radius corrections so visual area is parity
 * across pentagon/hex/diamond/star/circle/etc.
 *
 * Lifted verbatim from the design handoff:
 *   `~/Downloads/design_handoff_echelon_update/environment/detail.jsx:826-835`
 *
 * Without this correction, diamonds read ~36% smaller than circles and
 * round-squares ~28% larger. The factors are √(π / k) where k is the
 * shape's area-per-radius² constant.
 *
 * cycle-102 sprint-2 Task 2.3 — sprint plan §307 + §345-346.
 */

export type NodeShape =
  | 'circle'
  | 'roundsquare'
  | 'diamond'
  | 'pentagon'
  | 'hex'
  | 'octagon'
  | 'triangle'
  | 'star';

export const AREA_SCALE: Record<NodeShape, number> = {
  circle: 1.0,
  roundsquare: 0.886, // √(π/4) — square has bigger area per radius
  diamond: 1.253, // √(π/2)
  pentagon: 1.15, // √(π/2.378)
  hex: 1.1, // √(π/2.598)
  octagon: 1.04, // ~ √(π/2.828)
  triangle: 1.35, // triangle packs poorly
  star: 1.25,
};

/**
 * Apply AREA_SCALE to a nominal node radius. Returns the corrected
 * radius such that the rendered shape has approximately the same visual
 * area as a circle of the same nominal radius.
 */
export function correctedRadius(nominalRadius: number, shape: NodeShape): number {
  return nominalRadius * AREA_SCALE[shape];
}

/**
 * Compute the rendered area of a shape at a given nominal radius after
 * AREA_SCALE correction. Used by area-parity tests for the analytically
 * tractable shapes — circle, pentagon, hex, diamond. The remaining
 * shapes (star, roundsquare, octagon, triangle) have empirical
 * AREA_SCALE values that target visual parity, not strict mathematical
 * area parity, so this function does NOT support them — it throws.
 *
 * The design's `AREA_SCALE.star = 1.25` is an eyeball compromise
 * between "convex-hull pentagon area" (over-corrects) and "actual
 * stroked star area" (under-corrects); see the inline comments at
 * `~/Downloads/design_handoff_echelon_update/environment/detail.jsx:826-835`.
 *
 * Analytic formulas (each shape inscribed in circle of radius r):
 *   - circle:      π r²
 *   - pentagon:    (5/2) r² sin(2π/5)
 *   - hex:         (3√3/2) r²
 *   - diamond:     2 r²            (square rotated 45°, diagonal = 2r)
 */
export function analyticShapeArea(
  nominalRadius: number,
  shape: 'circle' | 'pentagon' | 'hex' | 'diamond',
): number {
  const r = correctedRadius(nominalRadius, shape);
  switch (shape) {
    case 'circle':
      return Math.PI * r * r;
    case 'pentagon':
      return (5 / 2) * r * r * Math.sin((2 * Math.PI) / 5);
    case 'hex':
      return ((3 * Math.sqrt(3)) / 2) * r * r;
    case 'diamond':
      return 2 * r * r;
  }
}
